import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiStar } from 'react-icons/hi';

const ResultsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const results = [
    'Greater clarity and confidence',
    'Reduced stress and burnout',
    'Stronger leadership presence',
    'Improved team dynamics',
    'Sustainable performance growth',
  ];

  const testimonials = [
    {
      quote: "Working with Susana transformed how I lead my team. I went from constantly firefighting to strategic thinking. The clarity I gained has been invaluable.",
      author: "Maria K.",
      role: "VP of Operations, Tech Startup",
    },
    {
      quote: "Susana's approach helped us reduce team burnout by 40% while actually improving our output. She truly understands the balance between performance and well-being.",
      author: "David L.",
      role: "CEO, Marketing Agency",
    },
    {
      quote: "The leadership mentorship was exactly what I needed. I now have tools and frameworks I use daily. My confidence in decision-making has completely changed.",
      author: "Ana S.",
      role: "Director of People, Healthcare",
    },
  ];

  return (
    <section
      id="results"
      ref={ref}
      data-testid="results-section"
      className="section-padding bg-[#F3EFEB]"
    >
      <div className="container-main">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-[#7A8B71] mb-4">Results</p>
          <h2 className="heading-2 text-[#0A192F]" data-testid="results-headline">
            What Clients Experience
          </h2>
        </motion.div>

        {/* Results List */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 md:gap-6 mb-20"
        >
          {results.map((result, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-6 py-3 bg-[#FDFBF7] border border-[#0A192F]/10 rounded-full"
              data-testid={`result-item-${index + 1}`}
            >
              <span className="w-2 h-2 bg-[#7A8B71] rounded-full" />
              <span className="body-text text-[#0A192F]">{result}</span>
            </div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
              className="bg-[#FDFBF7] p-8 md:p-10 rounded-lg relative"
              data-testid={`testimonial-${index + 1}`}
            >
              {/* Quote mark */}
              <span className="quote-mark">"</span>

              {/* Stars */}
              <div className="flex gap-1 mb-6 relative z-10">
                {[...Array(5)].map((_, i) => (
                  <HiStar key={i} className="w-5 h-5 text-[#7A8B71]" />
                ))}
              </div>

              {/* Quote */}
              <p className="body-text text-[#4A5568] mb-6 relative z-10">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="relative z-10">
                <p className="font-medium text-[#0A192F]">{testimonial.author}</p>
                <p className="text-sm text-[#4A5568]">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
